import { userHasFeature } from 'refactor/hooks/useUserHasFeature'
import {
    resolveSeedingStream,
    resolveSignalQuestSnapshot,
    seedingControlsForMode,
    type SeedingMode,
} from 'lib/seedingStream'
import { getCachedQuest } from 'state/cache'
import useStore, { ITeam } from 'state/knovStore'
import type { Answer } from 'types/answers'
import type { Quest } from 'types/quests'

export { MIN_SEED_SATS, seedMinimumSats, seedQuickSats } from 'lib/seedMinimum'
export type { SeedingMode } from 'lib/seedingStream'

type SeedingContextOptions = {
    streams?: readonly Partial<ITeam>[]
    signalQuest?: Pick<Quest, 'id' | 'team' | 'team_id'> | null
}

function streamForAnswer(
    answer: Answer,
    streams?: readonly Partial<ITeam>[],
    signalQuest?: Pick<Quest, 'id' | 'team' | 'team_id'> | null,
): Partial<ITeam> | null {
    const questId = answer.signal_quest_id || answer.quest_id || answer.downtree_quest_id
    const cachedSignalQuest = questId ? getCachedQuest(String(questId)) : null
    const quest = resolveSignalQuestSnapshot(questId, signalQuest, cachedSignalQuest)
    const currentStreams =
        streams ??
        (() => {
            const store = useStore.getState()
            return [...(store.userStreams || []), ...(store.publicTeams || [])]
        })()
    return resolveSeedingStream(quest, currentStreams)
}

export function seedingContextForAnswer(
    answer: Answer,
    { streams, signalQuest }: SeedingContextOptions = {},
) {
    const stream = streamForAnswer(answer, streams, signalQuest)
    const mode = (stream?.seeding_mode || 'upvalue_only') as SeedingMode
    const featureEnabled = userHasFeature('seeding')

    return {
        streamId: stream?.id ? String(stream.id) : null,
        seedSelected: stream?.seed_selected === true,
        mode,
        ...seedingControlsForMode(mode, { featureEnabled }),
    }
}

export function useSeedingContextForAnswer(
    answer: Answer,
    signalQuest?: Pick<Quest, 'id' | 'team' | 'team_id'> | null,
) {
    const userStreams = useStore(state => state.userStreams)
    const publicTeams = useStore(state => state.publicTeams)

    return seedingContextForAnswer(answer, {
        streams: [...(userStreams || []), ...(publicTeams || [])],
        signalQuest,
    })
}
